import { lazy, useEffect, type ComponentType } from "react";
import { Routes, Route, useLocation } from "react-router";
import { useRouteTracking } from "./telemetry/routeTracker";
import { RootLayout } from "./components/layout/RootLayout";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { AdminLayout } from "./components/layout/AdminLayout";
import { ROUTES } from "./lib/constants";
import { getStaticRouteTitle } from "./lib/pageTitles";
import { RequireAuth } from "./auth/RequireAuth";
import { RequireAdmin } from "./auth/RequireAdmin";
import { LandingPage } from "./pages/landing/LandingPage";

function lazyRoute(load: () => Promise<ComponentType>) {
  return lazy(() => load().then((defaultComponent) => ({ default: defaultComponent })));
}

const LoginPage = lazyRoute(() => import("./pages/auth/LoginPage").then((module) => module.LoginPage));
const VerifyPage = lazyRoute(() => import("./pages/auth/VerifyPage").then((module) => module.VerifyPage));
const DashboardPage = lazyRoute(() => import("./pages/dashboard/DashboardPage").then((module) => module.DashboardPage));
const KeysPage = lazyRoute(() => import("./pages/dashboard/KeysPage").then((module) => module.KeysPage));
const BillingPage = lazyRoute(() => import("./pages/dashboard/BillingPage").then((module) => module.BillingPage));
const SuccessPage = lazyRoute(() => import("./pages/billing/SuccessPage").then((module) => module.SuccessPage));
const CancelPage = lazyRoute(() => import("./pages/billing/CancelPage").then((module) => module.CancelPage));
const UsersPage = lazyRoute(() => import("./pages/admin/UsersPage").then((module) => module.UsersPage));
const AbusePage = lazyRoute(() => import("./pages/admin/AbusePage").then((module) => module.AbusePage));
const UserDetailPage = lazyRoute(() => import("./pages/admin/UserDetailPage").then((module) => module.UserDetailPage));
const SubscriptionsPage = lazyRoute(() => import("./pages/admin/SubscriptionsPage").then((module) => module.SubscriptionsPage));
const UsagePage = lazyRoute(() => import("./pages/admin/UsagePage").then((module) => module.UsagePage));
const LogsPage = lazyRoute(() => import("./pages/admin/LogsPage").then((module) => module.LogsPage));
const EnterprisePage = lazyRoute(() => import("./pages/admin/EnterprisePage").then((module) => module.EnterprisePage));
const ContactEnterprisePage = lazyRoute(() =>
  import("./pages/landing/ContactEnterprisePage").then((module) => module.ContactEnterprisePage),
);
const SubmissionPage = lazyRoute(() => import("./pages/dashboard/SubmissionPage").then((module) => module.SubmissionPage));
const SupportPage = lazyRoute(() => import("./pages/dashboard/SupportPage").then((module) => module.SupportPage));
const WineHealthPage = lazyRoute(() => import("./pages/admin/WineHealthPage").then((module) => module.WineHealthPage));
const ProviderStatusPage = lazyRoute(() =>
  import("./pages/admin/ProviderStatusPage").then((module) => module.ProviderStatusPage),
);
const ApiTestsPage = lazyRoute(() => import("./pages/admin/ApiTestsPage").then((module) => module.ApiTestsPage));
const TermsPage = lazyRoute(() => import("./pages/legal/TermsPage").then((module) => module.TermsPage));
const PrivacyPage = lazyRoute(() => import("./pages/legal/PrivacyPage").then((module) => module.PrivacyPage));
const McpPage = lazyRoute(() => import("./pages/docs/McpPage").then((module) => module.McpPage));
const DataCoveragePage = lazyRoute(() => import("./pages/docs/DataCoveragePage").then((module) => module.DataCoveragePage));
const PostmortemPage = lazyRoute(() => import("./pages/postmortem/PostmortemPage").then((module) => module.PostmortemPage));

export function App() {
  useRouteTracking();
  const location = useLocation();

  useEffect(() => {
    const title = getStaticRouteTitle(location.pathname);
    if (title) {
      document.title = title;
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route index element={<LandingPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="verify" element={<VerifyPage />} />
        <Route path="contact-enterprise" element={<ContactEnterprisePage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="mcp-setup" element={<McpPage />} />
        <Route path="data-coverage" element={<DataCoveragePage />} />
        <Route path="postmortem/:slug" element={<PostmortemPage />} />
        <Route path="billing/success" element={<SuccessPage />} />
        <Route path="billing/cancel" element={<CancelPage />} />
        <Route
          path="dashboard"
          element={
            <RequireAuth>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="keys" element={<KeysPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="submissions/new" element={<SubmissionPage />} />
          <Route path="support" element={<SupportPage />} />
        </Route>
        <Route
          path="admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<UsersPage />} />
          <Route path={ROUTES.ADMIN_ABUSE.replace("/admin/", "")} element={<AbusePage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="logs" element={<LogsPage />} />
          <Route path="enterprise" element={<EnterprisePage />} />
          <Route path="wine-health" element={<WineHealthPage />} />
          <Route path="provider-status" element={<ProviderStatusPage />} />
          <Route path="api-tests" element={<ApiTestsPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
